<template>
  <BaseModal
    :is-open="isOpen"
    title="Action Request"
    max-width="550px"
    :closable="false"
  >
    <div class="creator-action-request-container">
      <!-- Requester Info -->
      <div class="requester-info">
        <div class="requester-avatar">
          <img
            v-if="requesterData?.profile_picture"
            :src="`/images/profiles/${requesterData.profile_picture}.png`"
            :alt="requesterData.player_name"
            class="avatar-image"
          />
          <div v-else class="avatar-initials">
            {{ getInitials(requesterData?.player_name || '?') }}
          </div>
        </div>

        <div class="requester-details">
          <h3 class="requester-name">{{ requesterData?.player_name }}</h3>
          <p class="request-description">wants to perform the following action:</p>
        </div>
      </div>

      <!-- Action Details -->
      <div class="action-details">
        <div class="action-icon">
          <span class="material-symbols-outlined">{{ actionIcon }}</span>
        </div>
        <div class="action-info">
          <h4 class="action-title">{{ actionTitle }}</h4>
          <p class="action-description">{{ actionDescription }}</p>
        </div>
      </div>

      <!-- Impact Warning (if applicable) -->
      <div v-if="hasImpactWarning" class="impact-warning">
        <span class="material-symbols-outlined">warning</span>
        <span>{{ impactWarning }}</span>
      </div>

      <!-- Approval Actions -->
      <div class="approval-actions">
        <button @click="handleDeny" class="btn-deny">
          <span class="material-symbols-outlined">block</span>
          <span>Deny Request</span>
        </button>
        <button @click="handleApprove" class="btn-approve">
          <span class="material-symbols-outlined">check_circle</span>
          <span>Approve Request</span>
        </button>
      </div>

      <!-- Info Note -->
      <p class="info-note">
        <span class="material-symbols-outlined">info</span>
        <span>As game creator, only you can approve this action.</span>
      </p>
    </div>
  </BaseModal>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import BaseModal from './BaseModal.vue'

/**
 * CreatorActionRequestModal Component
 *
 * Shown to game creator when a non-creator player requests a creator-only action.
 * Creator can approve or deny the request.
 *
 * Props:
 * - action: Type of action requested (end_game, end_round, restart)
 * - requester_id: ID of requesting player
 * - requester_name: Name of requesting player
 * - requester_profile_picture: Requester's avatar
 * - request_id: Unique ID for this request
 *
 * Emits:
 * - approve: { request_id: string, action: string } - Request approved
 * - deny: { request_id: string, action: string } - Request denied
 */

type CreatorAction = 'end_game' | 'end_round' | 'restart_game'

interface Props {
  isOpen: boolean
  data: {
    action?: CreatorAction
    requester_id?: string
    requester_name?: string
    requester_profile_picture?: string
    request_id?: string
  } | null
}

const props = withDefaults(defineProps<Props>(), {
  isOpen: false,
  data: null,
})

const emit = defineEmits<{
  approve: [{ request_id: string; action: string }]
  deny: [{ request_id: string; action: string }]
  close: []
}>()

/**
 * Requester data
 */
const requesterData = computed(() => ({
  player_name: props.data?.requester_name || 'A player',
  profile_picture: props.data?.requester_profile_picture,
}))

/**
 * Action details based on type
 */
const actionConfig = computed(() => {
  const action = props.data?.action || 'end_game'

  const configs: Record<
    CreatorAction,
    {
      icon: string
      title: string
      description: string
      warning?: string
    }
  > = {
    end_game: {
      icon: 'stop_circle',
      title: 'End Game',
      description: 'Immediately end the current game and return all players to the lobby.',
      warning: 'All current progress will be lost. Scores will not be saved.',
    },
    end_round: {
      icon: 'skip_next',
      title: 'End Round',
      description: 'End the current round and proceed directly to scoring.',
      warning: 'The round will end immediately, regardless of current game state.',
    },
    restart_game: {
      icon: 'restart_alt',
      title: 'Restart Game',
      description: 'Reset the game to the beginning with all scores cleared.',
      warning: 'All progress and scores will be reset. Players will keep their current seats.',
    },
  }

  return configs[action] || configs.end_game
})

/**
 * Action icon
 */
const actionIcon = computed(() => actionConfig.value.icon)

/**
 * Action title
 */
const actionTitle = computed(() => actionConfig.value.title)

/**
 * Action description
 */
const actionDescription = computed(() => actionConfig.value.description)

/**
 * Impact warning
 */
const impactWarning = computed(() => actionConfig.value.warning || '')

/**
 * Whether action has impact warning
 */
const hasImpactWarning = computed(() => !!impactWarning.value)

/**
 * Get initials from name
 */
function getInitials(name: string): string {
  return name
    .split(' ')
    .map((word) => word[0])
    .join('')
    .toUpperCase()
    .slice(0, 2)
}

/**
 * Handle approve
 */
function handleApprove(): void {
  if (props.data?.request_id && props.data?.action) {
    emit('approve', {
      request_id: props.data.request_id,
      action: props.data.action,
    })
  }
  emit('close')
}

/**
 * Handle deny
 */
function handleDeny(): void {
  if (props.data?.request_id && props.data?.action) {
    emit('deny', {
      request_id: props.data.request_id,
      action: props.data.action,
    })
  }
  emit('close')
}
</script>

<style scoped>
.creator-action-request-container {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  padding: 0.5rem;
}

/* Requester Info */
.requester-info {
  display: flex;
  align-items: center;
  gap: 1.25rem;
  padding: 1.25rem;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 12px;
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.requester-avatar {
  width: 64px;
  height: 64px;
  border-radius: 50%;
  overflow: hidden;
  flex-shrink: 0;
  border: 3px solid rgba(232, 197, 71, 0.3);
}

.avatar-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.avatar-initials {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #e8c547, #d4af37);
  color: #0d1f2d;
  font-size: 1.5rem;
  font-weight: 700;
}

.requester-details {
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
  flex: 1;
}

.requester-name {
  font-size: 1.25rem;
  font-weight: 700;
  color: rgba(255, 255, 255, 0.95);
  margin: 0;
}

.request-description {
  font-size: 0.9375rem;
  color: rgba(255, 255, 255, 0.7);
  margin: 0;
}

/* Action Details */
.action-details {
  display: flex;
  gap: 1.25rem;
  padding: 1.5rem;
  background: linear-gradient(135deg, rgba(232, 197, 71, 0.1), rgba(212, 175, 55, 0.1));
  border: 1px solid rgba(232, 197, 71, 0.3);
  border-radius: 12px;
}

.action-icon {
  width: 64px;
  height: 64px;
  border-radius: 50%;
  background: linear-gradient(135deg, rgba(232, 197, 71, 0.2), rgba(212, 175, 55, 0.2));
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.action-icon .material-symbols-outlined {
  font-size: 2rem;
  color: #e8c547;
}

.action-info {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  flex: 1;
}

.action-title {
  font-size: 1.25rem;
  font-weight: 700;
  color: #e8c547;
  margin: 0;
}

.action-description {
  font-size: 0.9375rem;
  color: rgba(255, 255, 255, 0.85);
  line-height: 1.6;
  margin: 0;
}

/* Impact Warning */
.impact-warning {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  padding: 1rem;
  background: rgba(251, 191, 36, 0.1);
  border: 1px solid rgba(251, 191, 36, 0.3);
  border-radius: 8px;
  font-size: 0.9375rem;
  color: rgba(251, 191, 36, 0.9);
  line-height: 1.5;
}

.impact-warning .material-symbols-outlined {
  font-size: 1.25rem;
  flex-shrink: 0;
  margin-top: 0.125rem;
}

/* Approval Actions */
.approval-actions {
  display: flex;
  gap: 1rem;
  margin-top: 0.5rem;
}

.btn-deny,
.btn-approve {
  flex: 1;
  padding: 0.875rem 1.5rem;
  border-radius: 12px;
  font-weight: 600;
  font-size: 0.875rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  border: none;
}

.btn-deny {
  background: rgba(239, 68, 68, 0.15);
  color: #f87171;
  border: 1px solid rgba(239, 68, 68, 0.3);
}

.btn-deny:hover {
  background: rgba(239, 68, 68, 0.25);
  border-color: rgba(239, 68, 68, 0.5);
  transform: translateY(-2px);
}

.btn-approve {
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  color: white;
  box-shadow: 0 4px 12px rgba(16, 185, 129, 0.3);
}

.btn-approve:hover {
  background: linear-gradient(135deg, #34d399 0%, #10b981 100%);
  box-shadow: 0 6px 16px rgba(16, 185, 129, 0.4);
  transform: translateY(-2px);
}

.btn-approve:active,
.btn-deny:active {
  transform: translateY(0);
}

.btn-deny .material-symbols-outlined,
.btn-approve .material-symbols-outlined {
  font-size: 1.25rem;
}

/* Info Note */
.info-note {
  display: flex;
  align-items: flex-start;
  gap: 0.625rem;
  padding: 0.75rem 1rem;
  background: rgba(59, 130, 246, 0.1);
  border: 1px solid rgba(59, 130, 246, 0.2);
  border-radius: 8px;
  font-size: 0.875rem;
  color: rgba(147, 197, 253, 0.9);
  line-height: 1.5;
  margin: 0;
}

.info-note .material-symbols-outlined {
  font-size: 1.125rem;
  flex-shrink: 0;
  margin-top: 0.125rem;
}

/* Responsive */
@media (max-width: 640px) {
  .requester-info {
    flex-direction: column;
    text-align: center;
  }

  .requester-details {
    align-items: center;
  }

  .action-details {
    flex-direction: column;
    text-align: center;
  }

  .action-info {
    align-items: center;
  }

  .approval-actions {
    flex-direction: column;
  }
}
</style>
